import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, Loader2 } from "lucide-react";

const CONCERNS = ["Hair Fall", "Dandruff", "Dryness / Frizz", "Thinning", "Scalp Irritation", "General Care"];
const SYSTEMS = ["Fusion", "Ayurveda", "Allopathy"];

const toISO = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

export default function Consultation() {
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", concern: "", system_preference: "Fusion", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!date) return;
    setSlot("");
    api.get("/consultations/availability", { params: { date: toISO(date) } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) return toast.error("Please select a date and time slot.");
    if (!form.name || !form.email || !form.phone || !form.concern)
      return toast.error("Please fill in all required fields.");
    setLoading(true);
    try {
      await api.post("/consultations", { ...form, date: toISO(date), time_slot: slot });
      setDone(true);
      toast.success("Consultation booked! Check your email for confirmation.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="pt-40 pb-32 min-h-screen bg-cream flex items-center justify-center px-6" data-testid="consult-success">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 mx-auto border border-amber rounded-full flex items-center justify-center text-amber-deep"><Check className="w-7 h-7" /></div>
          <h1 className="mt-8 font-serif text-5xl text-obsidian">You&apos;re booked.</h1>
          <p className="mt-4 font-sans font-light text-obsidian/70">
            A confirmation has been sent to <b>{form.email}</b>. Our trichologist will
            reach out shortly to finalise your {form.system_preference.toLowerCase()} session.
          </p>
        </div>
      </div>
    );

  return (
    <div className="pt-32 md:pt-40 pb-24 bg-cream min-h-screen" data-testid="consultation-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="max-w-2xl">
          <span className="font-sans uppercase tracking-[0.35em] text-[11px] text-amber-deep">Online Consultation</span>
          <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[0.9] text-obsidian">
            Book your<br /><span className="italic">trichologist.</span>
          </h1>
          <p className="mt-6 font-sans font-light text-obsidian/70 max-w-lg leading-relaxed">
            A private video session to diagnose your hair &amp; scalp concern and design
            a personalised allopathy + ayurveda regimen.
          </p>
        </div>

        <form onSubmit={submit} className="mt-14 grid lg:grid-cols-2 gap-12">
          {/* Left: calendar + slots */}
          <div>
            <h3 className="font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/60 mb-4">1 · Choose a date</h3>
            <div className="border border-black/10 bg-cream p-2 inline-block" data-testid="consult-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
              />
            </div>

            {date ? (
              <div className="mt-8">
                <h3 className="font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/60 mb-4">2 · Choose a time</h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {slots.map((s) => (
                    <button
                      type="button"
                      key={s.time}
                      disabled={!s.available}
                      data-testid={`slot-${s.time.replace(/[:\s]/g, "")}`}
                      onClick={() => setSlot(s.time)}
                      className={`py-3 font-sans text-[12px] border transition-colors ${
                        slot === s.time
                          ? "bg-obsidian text-cream border-obsidian"
                          : s.available
                          ? "border-black/15 hover:border-obsidian"
                          : "border-black/5 text-black/25 line-through cursor-not-allowed"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-8">
                <h3 className="font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/40 mb-2">2 · Choose a time</h3>
                <p className="font-sans font-light text-sm text-obsidian/40">Select a date first to see available time slots.</p>
              </div>
            )}
          </div>

          {/* Right: details */}
          <div>
            <h3 className="font-sans uppercase tracking-[0.2em] text-[11px] text-obsidian/60 mb-4">3 · Your details</h3>
            <div className="space-y-4">
              <Input data-testid="consult-name" placeholder="Full name *" value={form.name} onChange={set("name")} className="rounded-none border-black/20 h-12" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="consult-email" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="rounded-none border-black/20 h-12" />
                <Input data-testid="consult-phone" placeholder="Phone *" value={form.phone} onChange={set("phone")} className="rounded-none border-black/20 h-12" />
              </div>
              <Select value={form.concern} onValueChange={set("concern")}>
                <SelectTrigger data-testid="consult-concern" className="rounded-none border-black/20 h-12"><SelectValue placeholder="Primary concern *" /></SelectTrigger>
                <SelectContent>{CONCERNS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.system_preference} onValueChange={set("system_preference")}>
                <SelectTrigger data-testid="consult-system" className="rounded-none border-black/20 h-12"><SelectValue placeholder="Preferred approach" /></SelectTrigger>
                <SelectContent>{SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea data-testid="consult-message" placeholder="Anything else we should know?" value={form.message} onChange={set("message")} className="rounded-none border-black/20 min-h-28" />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="consult-submit-btn"
              className="mt-6 w-full bg-obsidian text-cream py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-amber-deep transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm booking
            </button>
            {date && slot && (
              <p className="mt-4 font-sans text-sm text-obsidian/60">
                Selected: <b>{toISO(date)}</b> at <b>{slot}</b>
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
